import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Autocomplete,
  Chip,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  PersonAdd as PersonAddIcon,
  Edit as EditIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import api from '../../../api';
import { toast } from 'react-toastify';

const baseSignupFields = {
  parent_email: yup.string().email('Invalid email').required('Email is required'),
  parent_first_name: yup.string().required('First name is required'),
  parent_last_name: yup.string().required('Last name is required'),
  parent_country: yup.string().required('Country/Region is required'),
  parent_street_address: yup.string().required('Street address is required'),
  parent_town_city: yup.string().required('Town/City is required'),
  parent_postcode: yup.string().required('Postcode is required').min(3, 'Enter a valid postcode'),
  phone: yup.string().required('Phone is required'),
  student_first_name: yup.string().required('First name is required'),
  student_last_name: yup.string().required('Last name is required'),
  student_gender: yup.string().required('Gender is required'),
  student_date_of_birth: yup.string().required('Date of birth is required'),
  student_school_year_id: yup.string().required('School year is required'),
  notes: yup.string(),
};

const createSignupSchema = yup.object().shape(baseSignupFields);

const createDefaults = {
  parent_email: '',
  parent_first_name: '',
  parent_last_name: '',
  parent_country: '',
  parent_street_address: '',
  parent_town_city: '',
  parent_postcode: '',
  phone: '',
  student_first_name: '',
  student_last_name: '',
  student_gender: '',
  student_date_of_birth: '',
  student_school_year_id: '',
  notes: '',
};

function parseLocalPhoneDigits(fullPhone, code, numberLength) {
  if (!fullPhone) return '';
  const digits = String(fullPhone).replace(/\D/g, '');
  const codeDigits = String(code || '').replace(/\D/g, '');
  if (codeDigits && digits.startsWith(codeDigits)) {
    return digits.slice(codeDigits.length).slice(0, numberLength);
  }
  return digits.slice(-numberLength);
}

const StudentForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [dropdowns, setDropdowns] = useState({
    regions: [],
    schoolYears: [],
    schools: [],
  });
  const [dropdownLoading, setDropdownLoading] = useState(true);
  const [phoneCode, setPhoneCode] = useState('+44');
  const [phoneNumberLength, setPhoneNumberLength] = useState(10);
  const [targetSchoolsPick, setTargetSchoolsPick] = useState([]);
  /** Portal fields removed from UI on edit; still sent unchanged to satisfy API. */
  const [editPortalSnapshot, setEditPortalSnapshot] = useState(null);

  const resolver = useMemo(() => yupResolver(createSignupSchema), []);

  const { control, handleSubmit, formState: { errors }, reset } = useForm({
    resolver,
    defaultValues: createDefaults,
  });

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setDropdownLoading(true);
      try {
        const [
          regionsRes,
          schoolYearsRes,
          schoolsListRes,
          phoneRes,
        ] = await Promise.all([
          api.get('common/data?param=Regions'),
          api.get('common/data?param=SchoolYears'),
          api.get('common/data?param=Schools'),
          api.get('common/country-phone-code'),
        ]);

        if (cancelled) return;

        const phoneData = phoneRes.data?.success ? phoneRes.data.data || {} : {};
        const code = phoneData.phone_code || '+44';
        const len = phoneData.phone_number_length || 10;
        setPhoneCode(code);
        setPhoneNumberLength(len);

        const schools = schoolsListRes.data.data || [];
        setDropdowns({
          regions: regionsRes.data.data || [],
          schoolYears: schoolYearsRes.data.data || [],
          schools,
        });

        if (isEditMode && id) {
          setLoading(true);
          const response = await api.get(`admin/student/${id}/edit`);
          if (cancelled || !response.data.success) {
            if (!cancelled) setEditPortalSnapshot(null);
            return;
          }

          const data = response.data.data;
          const ids = Array.isArray(data.target_school_ids) ? data.target_school_ids : [];
          const pick = schools.filter((s) => ids.includes(s.id));
          setTargetSchoolsPick(pick);

          setEditPortalSnapshot({
            display_name: data.display_name || '',
            show_answer_after_n_attempts: data.show_answer_after_n_attempts ?? 2,
            allow_view_examiner_report_for_mocks: data.allow_view_examiner_report_for_mocks ?? true,
            can_change_password: data.can_change_password ?? true,
          });

          const localDigits = parseLocalPhoneDigits(data.parent?.phone, code, len);

          reset({
            parent_email: data.parent?.email || '',
            parent_first_name: data.parent?.first_name || '',
            parent_last_name: data.parent?.last_name || '',
            parent_country: data.region?.name || '',
            parent_street_address: data.parent_street_address || '',
            parent_town_city: data.parent_town_city || '',
            parent_postcode: data.parent_postcode || '',
            phone: localDigits,
            student_first_name: data.student_user?.first_name || '',
            student_last_name: data.student_user?.last_name || '',
            student_gender: data.student_gender || '',
            student_date_of_birth: data.student_date_of_birth || '',
            student_school_year_id: data.school_year?.id ? String(data.school_year.id) : '',
            notes: data.notes != null && data.notes !== '' ? data.notes : (data.bio || ''),
          });
        } else {
          setEditPortalSnapshot(null);
        }
      } catch (error) {
        console.error('Error loading form:', error);
        toast.error('Failed to load form');
        if (isEditMode) navigate('/admin/students');
      } finally {
        if (!cancelled) {
          setDropdownLoading(false);
          setLoading(false);
        }
      }
    };

    load();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- load once per id / mode
  }, [id, isEditMode, navigate]);

  const validateCreatePhone = (digits) => {
    if (digits.length !== phoneNumberLength) {
      toast.error(`Phone must be exactly ${phoneNumberLength} digits`);
      return false;
    }
    return true;
  };

  const onSubmit = async (data) => {
    const digits = String(data.phone || '').replace(/\D/g, '');
    if (!validateCreatePhone(digits)) return;

    if (!isEditMode) {
      try {
        setLoading(true);
        const targetIds = targetSchoolsPick.map((s) => s.id);
        const payload = {
          parent_first_name: data.parent_first_name,
          parent_last_name: data.parent_last_name,
          parent_email: data.parent_email,
          parent_phone: `${phoneCode}${digits}`,
          parent_country: data.parent_country,
          parent_street_address: data.parent_street_address,
          parent_town_city: data.parent_town_city,
          parent_postcode: data.parent_postcode,
          student_first_name: data.student_first_name,
          student_last_name: data.student_last_name,
          student_gender: data.student_gender,
          student_date_of_birth: data.student_date_of_birth,
          student_school_year_id: Number(data.student_school_year_id),
          target_school_ids: targetIds,
          notes: data.notes || '',
        };

        const response = await api.post('admin/student', payload);

        if (response.data.success) {
          toast.success(response.data.message || 'Student created successfully');
          navigate('/admin/students');
        }
      } catch (error) {
        console.error('Error saving student:', error);
        if (error.response?.data?.errors) {
          Object.entries(error.response.data.errors).forEach(([field, messages]) => {
            toast.error(`${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`);
          });
        } else {
          toast.error(error.response?.data?.message || 'Failed to create student');
        }
      } finally {
        setLoading(false);
      }
      return;
    }

    if (!editPortalSnapshot) {
      toast.error('Form not ready. Please wait for the student to load.');
      return;
    }

    try {
      setLoading(true);

      const targetIds = targetSchoolsPick.map((s) => s.id);
      const payload = {
        parent_first_name: data.parent_first_name,
        parent_last_name: data.parent_last_name,
        parent_phone: `${phoneCode}${digits}`,
        parent_country: data.parent_country,
        parent_street_address: data.parent_street_address,
        parent_town_city: data.parent_town_city,
        parent_postcode: data.parent_postcode,
        student_first_name: data.student_first_name,
        student_last_name: data.student_last_name,
        student_gender: data.student_gender,
        student_date_of_birth: data.student_date_of_birth,
        student_school_year_id: Number(data.student_school_year_id),
        target_school_ids: targetIds,
        notes: data.notes || '',
        display_name: editPortalSnapshot.display_name,
        show_answer_after_n_attempts: parseInt(String(editPortalSnapshot.show_answer_after_n_attempts), 10),
        allow_view_examiner_report_for_mocks: editPortalSnapshot.allow_view_examiner_report_for_mocks,
        can_change_password: editPortalSnapshot.can_change_password,
      };

      const response = await api.put(`admin/student/${id}`, payload);

      if (response.data.success) {
        toast.success('Student updated successfully');
        navigate('/admin/students');
      }
    } catch (error) {
      console.error('Error saving student:', error);
      if (error.response?.data?.errors) {
        Object.entries(error.response.data.errors).forEach(([field, messages]) => {
          toast.error(`${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`);
        });
      } else {
        toast.error('Failed to update student');
      }
    } finally {
      setLoading(false);
    }
  };

  if (dropdownLoading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography>Loading form...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{
      p: 3,
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
    }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/admin/students')}
        sx={{
          mb: 3,
          color: '#5a6c7d',
          fontWeight: 600,
          '&:hover': { bgcolor: 'rgba(0,0,0,0.05)' }
        }}
      >
        Back to Student List
      </Button>

      <Box sx={{ mb: 4 }}>
        <Box sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 2,
          mb: 2,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: '25px',
          px: 3,
          py: 1,
          boxShadow: '0 4px 15px rgba(102, 126, 234, 0.2)'
        }}>
          {isEditMode ? <EditIcon sx={{ color: 'white' }} /> : <PersonAddIcon sx={{ color: 'white' }} />}
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'white' }}>
            {isEditMode ? 'Edit Student' : 'Add New Student'}
          </Typography>
        </Box>
        <Typography variant="body1" sx={{ color: '#5a6c7d', ml: 1 }}>
          {isEditMode
            ? 'Same sections as create: parent, child, school year, target schools, and notes.'
            : 'Same details as the website registration. New accounts receive a verification email; login details are sent after verification.'}
        </Typography>
      </Box>

      <Card sx={{
        borderRadius: 4,
        boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
        background: 'rgba(255,255,255,0.95)',
        backdropFilter: 'blur(20px)'
      }}>
        <CardContent sx={{ p: 4 }}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#2d3748' }}>
                  1. Parent details
                </Typography>
                <Divider sx={{ mb: 3 }} />
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name="parent_email"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Parent email *"
                      type="email"
                      fullWidth
                      disabled={isEditMode}
                      InputProps={isEditMode ? { readOnly: true } : undefined}
                      error={!!errors.parent_email}
                      helperText={isEditMode ? 'Email cannot be changed' : errors.parent_email?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6} />
              <Grid item xs={12} md={6}>
                <Controller
                  name="parent_first_name"
                  control={control}
                  render={({ field }) => (
                    <TextField {...field} label="Parent first name *" fullWidth error={!!errors.parent_first_name} helperText={errors.parent_first_name?.message} />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="parent_last_name"
                  control={control}
                  render={({ field }) => (
                    <TextField {...field} label="Parent last name *" fullWidth error={!!errors.parent_last_name} helperText={errors.parent_last_name?.message} />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="parent_country"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors.parent_country}>
                      <InputLabel>Country / region *</InputLabel>
                      <Select {...field} label="Country / region *">
                        <MenuItem value=""><em>Select</em></MenuItem>
                        {dropdowns.regions.map((region) => (
                          <MenuItem key={region.id} value={region.name}>{region.name}</MenuItem>
                        ))}
                      </Select>
                      {errors.parent_country && <Typography variant="caption" color="error">{errors.parent_country.message}</Typography>}
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="parent_street_address"
                  control={control}
                  render={({ field }) => (
                    <TextField {...field} label="Street address *" fullWidth error={!!errors.parent_street_address} helperText={errors.parent_street_address?.message} />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="parent_town_city"
                  control={control}
                  render={({ field }) => (
                    <TextField {...field} label="Town / city *" fullWidth error={!!errors.parent_town_city} helperText={errors.parent_town_city?.message} />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="parent_postcode"
                  control={control}
                  render={({ field }) => (
                    <TextField {...field} label="Postcode *" fullWidth error={!!errors.parent_postcode} helperText={errors.parent_postcode?.message} />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>Phone *</Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField disabled value={phoneCode} sx={{ width: 100 }} />
                  <Controller
                    name="phone"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        placeholder={`${phoneNumberLength} digits`}
                        onChange={(e) => {
                          const d = e.target.value.replace(/\D/g, '').slice(0, phoneNumberLength);
                          field.onChange(d);
                        }}
                        error={!!errors.phone}
                        helperText={errors.phone?.message || `Digits only (${phoneNumberLength})`}
                      />
                    )}
                  />
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mt: 2, mb: 2, fontWeight: 600, color: '#2d3748' }}>
                  2. Child details
                </Typography>
                <Divider sx={{ mb: 3 }} />
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name="student_first_name"
                  control={control}
                  render={({ field }) => (
                    <TextField {...field} label="Child first name *" fullWidth error={!!errors.student_first_name} helperText={errors.student_first_name?.message} />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="student_last_name"
                  control={control}
                  render={({ field }) => (
                    <TextField {...field} label="Child last name *" fullWidth error={!!errors.student_last_name} helperText={errors.student_last_name?.message} />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="student_gender"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors.student_gender}>
                      <InputLabel>Gender *</InputLabel>
                      <Select {...field} label="Gender *">
                        <MenuItem value=""><em>Select</em></MenuItem>
                        <MenuItem value="Male">Male</MenuItem>
                        <MenuItem value="Female">Female</MenuItem>
                        <MenuItem value="Other">Other</MenuItem>
                      </Select>
                      {errors.student_gender && <Typography variant="caption" color="error">{errors.student_gender.message}</Typography>}
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="student_date_of_birth"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Date of birth *"
                      type="date"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      error={!!errors.student_date_of_birth}
                      helperText={errors.student_date_of_birth?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="student_school_year_id"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors.student_school_year_id}>
                      <InputLabel>School year *</InputLabel>
                      <Select {...field} label="School year *">
                        <MenuItem value=""><em>Select</em></MenuItem>
                        {dropdowns.schoolYears.map((sy) => (
                          <MenuItem key={sy.id} value={String(sy.id)}>{sy.name}</MenuItem>
                        ))}
                      </Select>
                      {errors.student_school_year_id && <Typography variant="caption" color="error">{errors.student_school_year_id.message}</Typography>}
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mt: 2, mb: 2, fontWeight: 600, color: '#2d3748' }}>
                  3. Additional information
                </Typography>
                <Divider sx={{ mb: 3 }} />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="body2" sx={{ mb: 1 }}>Target schools (optional)</Typography>
                <Autocomplete
                  multiple
                  options={dropdowns.schools}
                  getOptionLabel={(option) => option.name || ''}
                  isOptionEqualToValue={(a, b) => a.id === b.id}
                  value={targetSchoolsPick}
                  onChange={(_, newValue) => setTargetSchoolsPick(newValue)}
                  renderInput={(params) => <TextField {...params} placeholder="Select schools" variant="outlined" />}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip label={option.name} {...getTagProps({ index })} key={option.id} />
                    ))
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="notes"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Notes (optional)"
                      multiline
                      rows={4}
                      fullWidth
                      helperText={isEditMode ? 'Stored as free-text notes for this student.' : undefined}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
                  <Button
                    variant="outlined"
                    onClick={() => navigate('/admin/students')}
                    disabled={loading}
                  >
                    Cancel
                  </Button>

                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={<SaveIcon />}
                    disabled={loading}
                    sx={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      px: 4,
                      '&:hover': {
                        background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
                      }
                    }}
                  >
                    {loading ? 'Saving...' : (isEditMode ? 'Update Student' : 'Create Student')}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default StudentForm;
